import { useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getCssValue, getThemeAwareCssValue } from "../../charts/utils";
import { getTooltipCursorStyle } from "../../colors";
import {
    ChartDataTable,
    CustomLegend,
    LegendDashIcon,
    LegendLineIcon,
    MarketChartTooltip,
    XAxisTick,
    YAxisTick,
} from "../../components";
import { buildXAxisYears, shouldShowXAxisYear, TOOLTIP_OFFSET } from "../chart-constants";
import { formatVehicleValue, MARKET_LINE_LABELS, type MarketDataPoint } from "./market-chart-utils";

interface MarketChartProps {
    data: MarketDataPoint[];
    legendHeading: string;
}

export const MarketChart: React.FC<MarketChartProps> = ({ data, legendHeading }) => {
    const tooltipCursor = getTooltipCursorStyle();

    // Build the allowed year set used to show/hide tooltip content and the vertical cursor line: all years in yearly mode, or only 2022 + 5-year ticks in 5-year mode.
    const [activeYear, setActiveYear] = useState<number | null>(null);
    const xAxisTicks = buildXAxisYears(data.map((entry) => entry.year));
    const tooltipVisibleYears = new Set(xAxisTicks);
    const isCursorVisible = activeYear === null || shouldShowXAxisYear(activeYear);
    const colors = {
        totalbestand: getThemeAwareCssValue(
            "--ds-color-berggraa-visualisation-7",
            "--ds-color-text-subtle",
            "--ds-color-text-subtle",
        ),
        withEnova: getCssValue("--ds-color-fjordgronn-visualisation-10", "#00875d"),
        withoutEnova: getThemeAwareCssValue(
            "--ds-color-fjellgronn-visualisation-16",
            "--ds-color-fjellgronn-text-default",
        ),
    };
    const FLOAT_TOLERANCE = 1e-6;
    const hasTotalbestandSeries = data.some(({ totalbestand }) => Number.isFinite(totalbestand));
    const hasWithoutEnovaSeries = data.some(({ withoutEnova }) => Number.isFinite(withoutEnova));
    const hasWithEnovaSeries = data.some(({ withEnova, withoutEnova }) => {
        if (!Number.isFinite(withEnova)) {
            return false;
        }
        if (!Number.isFinite(withoutEnova)) {
            return true;
        }
        return Math.abs(withEnova - withoutEnova) > FLOAT_TOLERANCE;
    });

    const dataTableColumns = [
        { key: "year", label: "År", getValue: (row: MarketDataPoint) => String(row.year) },
        ...(hasTotalbestandSeries
            ? [
                  {
                      key: "totalbestand",
                      label: MARKET_LINE_LABELS.totalbestand,
                      getValue: (row: MarketDataPoint) => formatVehicleValue(row.totalbestand),
                  },
              ]
            : []),
        ...(hasWithEnovaSeries
            ? [
                  {
                      key: "withEnova",
                      label: MARKET_LINE_LABELS.withEnova,
                      getValue: (row: MarketDataPoint) => formatVehicleValue(row.withEnova),
                  },
              ]
            : []),
        ...(hasWithoutEnovaSeries
            ? [
                  {
                      key: "withoutEnova",
                      label: MARKET_LINE_LABELS.withoutEnova,
                      getValue: (row: MarketDataPoint) => formatVehicleValue(row.withoutEnova),
                  },
              ]
            : []),
    ];

    return (
        <>
            <ChartDataTable
                caption={legendHeading}
                columns={dataTableColumns}
                rowKey={(row) => String(row.year)}
                rows={data}
            />
            <ResponsiveContainer width="100%" height={600}>
                <LineChart
                    data={data}
                    margin={{ top: 0, right: 30, left: 0, bottom: 20 }}
                    onMouseMove={(state) => {
                        const year = Number(state?.activeLabel);
                        setActiveYear(Number.isFinite(year) ? year : null);
                    }}
                    onMouseLeave={() => setActiveYear(null)}
                >
                    <CartesianGrid strokeDasharray="1 1" vertical={false} syncWithTicks />

                    <XAxis
                        dataKey="year"
                        tick={<XAxisTick />}
                        interval={0}
                        ticks={xAxisTicks}
                        label={{
                            value: "År",
                            position: { x: 850, y: 4 },
                            fill: "var(--ds-color-neutral-text-default)",
                            fontSize: "var(--ds-font-size-1)",
                            fontWeight: 600,
                        }}
                    />

                    <YAxis
                        tick={<YAxisTick formatValue={(value: string | number) => formatVehicleValue(Number(value))} />}
                        domain={[0, "auto"]}
                        interval={0}
                        label={{
                            value: "Antall",
                            position: { x: 75, y: -10 },
                            fill: "var(--ds-color-neutral-text-default)",
                            fontSize: "var(--ds-font-size-1)",
                            fontWeight: 600,
                        }}
                        padding={{ top: 20, bottom: 0 }}
                    />

                    <Tooltip
                        content={(tooltipProps) => {
                            const year = Number(tooltipProps.label);
                            if (Number.isFinite(year) && !tooltipVisibleYears.has(year)) {
                                return null;
                            }

                            return (
                                <MarketChartTooltip
                                    active={tooltipProps.active}
                                    payload={tooltipProps.payload}
                                    label={tooltipProps.label}
                                />
                            );
                        }}
                        offset={TOOLTIP_OFFSET}
                        cursor={isCursorVisible ? tooltipCursor : false}
                    />

                    <Legend
                        verticalAlign="top"
                        wrapperStyle={{ paddingBottom: "40px" }}
                        content={
                            <CustomLegend heading={legendHeading}>
                                {hasTotalbestandSeries && (
                                    <LegendDashIcon label="Totalbestand" stroke={colors.totalbestand} />
                                )}
                                {hasWithEnovaSeries && (
                                    <LegendLineIcon
                                        label="Antall fossilfrie, utvikling med Enova"
                                        stroke={colors.withEnova}
                                    />
                                )}
                                {hasWithoutEnovaSeries && (
                                    <LegendLineIcon
                                        label="Antall fossilfrie, utvikling uten Enova"
                                        stroke={colors.withoutEnova}
                                    />
                                )}
                            </CustomLegend>
                        }
                    />

                    {hasTotalbestandSeries && (
                        <Line
                            activeDot={true} // show active dot for every year, even though cursor line and tooltip might only show every 5 years (see SHOW_X_AXIS_EVERY_5_YEARS)
                            type="linear"
                            dataKey="totalbestand"
                            name={MARKET_LINE_LABELS.totalbestand}
                            stroke={colors.totalbestand}
                            strokeWidth={3}
                            dot={false}
                            strokeDasharray="5 5"
                        />
                    )}
                    {hasWithEnovaSeries && (
                        <Line
                            activeDot={true}
                            type="linear"
                            dataKey="withEnova"
                            name={MARKET_LINE_LABELS.withEnova}
                            stroke={colors.withEnova}
                            strokeWidth={3}
                            dot={false}
                        />
                    )}
                    {hasWithoutEnovaSeries && (
                        <Line
                            activeDot={true}
                            type="linear"
                            dataKey="withoutEnova"
                            name={MARKET_LINE_LABELS.withoutEnova}
                            stroke={colors.withoutEnova}
                            strokeWidth={3}
                            dot={false}
                        />
                    )}
                </LineChart>
            </ResponsiveContainer>
        </>
    );
};
